import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  CreateTestStepRequest,
  SaveFullTestStepRequest,
  SaveFullTestStepResponse,
  TestStep,
  TestStepActivity,
  TestStepAttachment,
  TestStepUsage,
  UpdateTestStepRequest,
} from '../models/test-case.model';
import { Page } from '../models/page.model';

@Injectable({ providedIn: 'root' })
export class TestStepService {
  private http = inject(HttpClient);
  private base = `${environment.apiBaseUrl}/api/v1/projects`;

  private url(projectId: string, testCaseId: string): string {
    return `${this.base}/${projectId}/test-cases/${testCaseId}/steps`;
  }

  /** Modul-Scope-URL (NEX-593): Schritte werden analog zum Testfall-Kontext modul-scoped verwaltet. */
  private moduleUrl(projectId: string, moduleId: string): string {
    return `${this.base}/${projectId}/test-modules/${moduleId}/steps`;
  }

  list(projectId: string, testCaseId: string): Observable<TestStep[]> {
    return this.http.get<TestStep[]>(this.url(projectId, testCaseId));
  }

  create(projectId: string, testCaseId: string, request: CreateTestStepRequest): Observable<TestStep> {
    return this.http.post<TestStep>(this.url(projectId, testCaseId), request);
  }

  update(projectId: string, testCaseId: string, stepId: string, request: UpdateTestStepRequest): Observable<TestStep> {
    return this.http.patch<TestStep>(`${this.url(projectId, testCaseId)}/${stepId}`, request);
  }

  delete(projectId: string, testCaseId: string, stepId: string): Observable<void> {
    return this.http.delete<void>(`${this.url(projectId, testCaseId)}/${stepId}`);
  }

  reorder(projectId: string, testCaseId: string, orderedIds: string[]): Observable<TestStep[]> {
    return this.http.post<TestStep[]>(`${this.url(projectId, testCaseId)}/reorder`, { orderedIds });
  }

  copyFromModules(projectId: string, testCaseId: string, moduleIds: string[]): Observable<TestStep[]> {
    return this.http.post<TestStep[]>(`${this.url(projectId, testCaseId)}/from-modules`, { moduleIds });
  }

  /** Copy-on-use (NEX-537): kopiert einen Bibliotheks-Schritt als eigenen Schritt in den Testfall. */
  copyFromLibrary(projectId: string, testCaseId: string, libraryStepId: string): Observable<TestStep> {
    return this.http.post<TestStep>(`${this.url(projectId, testCaseId)}/from-library`, { libraryStepId });
  }

  listForModule(projectId: string, moduleId: string): Observable<TestStep[]> {
    return this.http.get<TestStep[]>(this.moduleUrl(projectId, moduleId));
  }

  createForModule(projectId: string, moduleId: string, request: CreateTestStepRequest): Observable<TestStep> {
    return this.http.post<TestStep>(this.moduleUrl(projectId, moduleId), request);
  }

  updateForModule(
    projectId: string,
    moduleId: string,
    stepId: string,
    request: UpdateTestStepRequest,
  ): Observable<TestStep> {
    return this.http.patch<TestStep>(`${this.moduleUrl(projectId, moduleId)}/${stepId}`, request);
  }

  deleteForModule(projectId: string, moduleId: string, stepId: string): Observable<void> {
    return this.http.delete<void>(`${this.moduleUrl(projectId, moduleId)}/${stepId}`);
  }

  reorderForModule(projectId: string, moduleId: string, orderedIds: string[]): Observable<TestStep[]> {
    return this.http.post<TestStep[]>(`${this.moduleUrl(projectId, moduleId)}/reorder`, { orderedIds });
  }

  /** Copy-on-use (NEX-462/593): kopiert einen Bibliotheks-Schritt in ein Modul. */
  copyFromLibraryToModule(projectId: string, moduleId: string, libraryStepId: string): Observable<TestStep> {
    return this.http.post<TestStep>(`${this.moduleUrl(projectId, moduleId)}/from-library`, { libraryStepId });
  }

  /** Basis-URL der projektweiten Schritt-Bibliothek (NEX-456). */
  private libraryUrl(projectId: string): string {
    return `${this.base}/${projectId}/test-steps`;
  }

  listLibrary(projectId: string): Observable<TestStep[]> {
    return this.http.get<TestStep[]>(this.libraryUrl(projectId));
  }

  createLibrary(projectId: string, request: CreateTestStepRequest): Observable<TestStep> {
    return this.http.post<TestStep>(this.libraryUrl(projectId), request);
  }

  updateLibrary(projectId: string, stepId: string, request: UpdateTestStepRequest): Observable<TestStep> {
    return this.http.patch<TestStep>(`${this.libraryUrl(projectId)}/${stepId}`, request);
  }

  deleteLibrary(projectId: string, stepId: string): Observable<void> {
    return this.http.delete<void>(`${this.libraryUrl(projectId)}/${stepId}`);
  }

  /** Buffered-Save (NEX-600/NEX-612): erstellt einen Bibliotheks-Schritt in einem einzigen POST. */
  createFullLibrary(projectId: string, request: SaveFullTestStepRequest): Observable<SaveFullTestStepResponse> {
    return this.http.post<SaveFullTestStepResponse>(`${this.libraryUrl(projectId)}/full`, request);
  }

  /** Buffered-Save (NEX-600/NEX-612): aktualisiert einen Bibliotheks-Schritt per Full-Replace-PUT. */
  saveFullLibrary(
    projectId: string,
    stepId: string,
    request: SaveFullTestStepRequest,
  ): Observable<SaveFullTestStepResponse> {
    return this.http.put<SaveFullTestStepResponse>(`${this.libraryUrl(projectId)}/${stepId}/full`, request);
  }

  /** Verwendung eines Bibliotheks-Schritts (NEX-463): einbindende Module + Testfälle. */
  getUsage(projectId: string, stepId: string): Observable<TestStepUsage> {
    return this.http.get<TestStepUsage>(`${this.libraryUrl(projectId)}/${stepId}/usage`);
  }

  /** Aktivitäts-Feed eines Bibliotheks-Schritts (NEX-459); liefert die Page-Inhalte. */
  listActivity(projectId: string, stepId: string): Observable<TestStepActivity[]> {
    return this.http
      .get<Page<TestStepActivity>>(`${this.libraryUrl(projectId)}/${stepId}/activity?page=0&size=20`)
      .pipe(map((page) => page.content));
  }

  private attachmentsUrl(projectId: string, stepId: string): string {
    return `${this.libraryUrl(projectId)}/${stepId}/attachments`;
  }

  listAttachments(projectId: string, stepId: string): Observable<TestStepAttachment[]> {
    return this.http.get<TestStepAttachment[]>(this.attachmentsUrl(projectId, stepId));
  }

  uploadAttachment(projectId: string, stepId: string, file: File): Observable<TestStepAttachment> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<TestStepAttachment>(this.attachmentsUrl(projectId, stepId), formData);
  }

  /** Lädt die Anhang-Bytes als Blob (Token via Interceptor) für einen client-seitigen Download. */
  downloadAttachment(projectId: string, stepId: string, attachmentId: string): Observable<Blob> {
    return this.http.get(`${this.attachmentsUrl(projectId, stepId)}/${attachmentId}`, { responseType: 'blob' });
  }

  deleteAttachment(projectId: string, stepId: string, attachmentId: string): Observable<void> {
    return this.http.delete<void>(`${this.attachmentsUrl(projectId, stepId)}/${attachmentId}`);
  }
}
